import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
} from '@mui/material';
import React from 'react';

interface VersionUpdateNotificationProps {
  hasUpdate: boolean;
  isForced: boolean;
  currentVersion?: string;
  latestVersion: string | null;
  updateMessage: string | null;
  onDismiss: () => void;
  onUpdate: () => void;
}

export const VersionUpdateNotification: React.FC<VersionUpdateNotificationProps> = ({
  hasUpdate,
  isForced,
  currentVersion,
  latestVersion,
  updateMessage,
  onDismiss,
  onUpdate,
}) => {
  if (!hasUpdate) return null;

  // Forced update - show modal dialog
  if (isForced) {
    return (
      <Dialog
        open={hasUpdate}
        onClose={() => {}} // Prevent closing
        disableEscapeKeyDown
        aria-labelledby="forced-update-dialog-title"
        aria-describedby="forced-update-dialog-description"
      >
        <DialogTitle id="forced-update-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <RefreshIcon color="primary" />
            Important Update Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="forced-update-dialog-description">
            {updateMessage ||
              'A critical update is available and must be installed to continue using the application.'}
            {latestVersion && (
              <Box mt={2}>
                <strong>New version:</strong> {latestVersion}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onUpdate} variant="contained" color="primary" autoFocus>
            Refresh Now
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Optional update - show dismissible snackbar
  return (
    <Snackbar
      open={hasUpdate}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ mt: 6 }} // Offset from top to avoid overlapping with header
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={onUpdate}
              startIcon={<RefreshIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Refresh Now
            </Button>
            <IconButton size="small" aria-label="dismiss" color="inherit" onClick={onDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {updateMessage ||
          'A new version is available! Refresh to get the latest updates and improvements.'}
        {latestVersion && currentVersion && (
          <Box component="span" ml={1} sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
            ({currentVersion} → {latestVersion})
          </Box>
        )}
      </Alert>
    </Snackbar>
  );
};
